import { FunctionComponent, SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './weekPatternSection.module.scss'
import { SelectOptionsDeprecated } from '@/types/Select'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import Tabs from '@/components/_custom/Tabs/tabs.tsx'
import { PariteType, UseWeekPatternReturn, WeekSubMode, MAX_CYCLE_SIZE as maxCycleSize } from './useWeekPattern'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'

interface WeekPatternSectionProps extends Omit<
    UseWeekPatternReturn,
    'chooseWeekPattern' | 'setChooseWeekPattern' | 'getMutationVariables'
> {
    selectedDays: string[]
    setSelectedDays: (days: string[]) => void
    selectDays: SelectOptionsDeprecated
}

const WeekPatternSection: FunctionComponent<WeekPatternSectionProps> = ({
    weekSubMode,
    setWeekSubMode,
    pariteType,
    setPariteType,
    weekCycle,
    weekOffsets,
    handleCycleSizeChange,
    handleWeekBoxClick,
    selectedDays,
    setSelectedDays,
    selectDays
}: WeekPatternSectionProps) => {
    const { t } = useTranslation()

    function handleDownCycleSize() {
        handleCycleSizeChange(weekCycle - 1)
    }
    function handleUpCycleSize() {
        handleCycleSizeChange(weekCycle + 1)
    }

    return (
        <div className={styles.WeekPatternSection}>
            <div className={styles.InputContainer}>
                <SelectMultiple
                    selectOptions={selectDays}
                    label={t('days')}
                    selectValues={selectedDays}
                    setSelectValues={setSelectedDays}
                    className={styles.Input}
                />
            </div>
            <div className={styles.SubModeTabs}>
                <Tabs
                    value={weekSubMode === WeekSubMode.parite ? 0 : 1}
                    onChange={(_e: SyntheticEvent, v: number): void =>
                        setWeekSubMode(v === 0 ? WeekSubMode.parite : WeekSubMode.cycle)
                    }
                    tabList={[t('byParity'), t('byCycle')]}
                />
            </div>
            {weekSubMode === WeekSubMode.parite && (
                <div className={styles.WeekCycleContainer}>
                    <div
                        className={`${styles.WeekBox} ${pariteType === PariteType.paire ? styles.WeekBoxActive : styles.WeekBoxInactive}`}
                        onClick={() => setPariteType(PariteType.paire)}
                    >
                        <span className={styles.WeekBoxLabel}>{t('evenWeeks')}</span>
                        <span className={styles.WeekBoxIcon}>{pariteType === PariteType.paire ? '✓' : '✗'}</span>
                    </div>
                    <div
                        className={`${styles.WeekBox} ${pariteType === PariteType.impaire ? styles.WeekBoxActive : styles.WeekBoxInactive}`}
                        onClick={() => setPariteType(PariteType.impaire)}
                    >
                        <span className={styles.WeekBoxLabel}>{t('oddWeeks')}</span>
                        <span className={styles.WeekBoxIcon}>{pariteType === PariteType.impaire ? '✓' : '✗'}</span>
                    </div>
                </div>
            )}
            {weekSubMode === WeekSubMode.cycle && (
                <>
                    <div className={styles.CycleSizeRow}>
                        <span className={styles.CycleSizeLabel}>{t('cycleLength')} :</span>
                        <ButtonIcon icon={<span>-</span>} onClick={handleDownCycleSize} />
                        <UserInput
                            className={styles.CyclesInput}
                            type='number'
                            value={weekCycle.toString()}
                            setValue={(value: string) => handleCycleSizeChange(Number(value))}
                            format='num'
                            validationDelay={600}
                            replaceAfterIdle={1000}
                            maxNumber={maxCycleSize}
                            minNumber={2}
                        />
                        <ButtonIcon icon={<span>+</span>} onClick={handleUpCycleSize} />
                        <span className={styles.CycleSizeUnit}>{t('weeks')}</span>
                    </div>
                    <div className={styles.WeekCycleContainer}>
                        {Array.from({ length: weekCycle }, (_, i) => {
                            const isActive: boolean = weekOffsets.includes(i)
                            return (
                                <div
                                    key={i}
                                    className={`${styles.WeekBox} ${isActive ? styles.WeekBoxActive : styles.WeekBoxInactive}`}
                                    onClick={() => handleWeekBoxClick(i)}
                                >
                                    <span className={styles.WeekBoxLabel}>
                                        {t('week')} {i + 1}
                                    </span>
                                    <span className={styles.WeekBoxIcon}>{isActive ? '✓' : '✗'}</span>
                                </div>
                            )
                        })}
                    </div>
                </>
            )}
        </div>
    )
}

export default WeekPatternSection
